import { useMemo } from 'react';

import useAnalogClock from '@/features/timeGame/hooks/useAnalogClock';
import useResponsive from '@/shared/hooks/useResponsive';

import AnswerAnalogBgMobile from '@/features/timeGame/assets/images/answer-analog-mobile.svg?react';
import AnswerAnalogBg from '@/features/timeGame/assets/images/answer-analog.svg?react';
import DragIndicator from '@/features/timeGame/assets/images/drag-indicator.svg?react';

import '@/features/timeGame/styles/analog-clock.scss';

interface AnalogClockProps {
	hours: number;
	minutes: number;
	onTimeChange?: (hours: number, minutes: number) => void;
	showBackground?: boolean;
	theme?: 'light' | 'dark';
	className?: string;
}

const AnalogClock = ({
	hours,
	minutes,
	onTimeChange,
	showBackground = false,
	theme = 'dark',
	className = '',
}: AnalogClockProps) => {
	const { isPortrait } = useResponsive();

	const {
		angles,
		clockRef,
		isDraggingHour,
		isDraggingMinute,
		handleHourMouseDown,
		handleMinuteMouseDown,
		handleHourTouchStart,
		handleMinuteTouchStart,
	} = useAnalogClock({
		hours,
		minutes,
		onTimeChange,
	});
	const isInteractive = Boolean(onTimeChange);
	const BackgroundComponent = isPortrait ? AnswerAnalogBgMobile : AnswerAnalogBg;

	// Generate clock numbers (1-12)
	const clockNumbers = useMemo(() => {
		return Array.from({ length: 12 }, (_, i) => {
			const number = i + 1;
			const angle = (number * 30 - 90) * (Math.PI / 180); // Convert to radians
			const radius = 36; // Distance from center
			const x = 50 + radius * Math.cos(angle);
			const y = 50 + radius * Math.sin(angle);

			return { number, x, y };
		});
	}, []);

	// Generate minute markers
	const minuteMarkers = useMemo(() => {
		return Array.from({ length: 60 }, (_, i) => {
			const angle = (i * 6 - 90) * (Math.PI / 180);
			const isHourMarker = i % 5 === 0;
			const innerRadius = 45;
			const outerRadius = 48;

			const x1 = 50 + innerRadius * Math.cos(angle);
			const y1 = 50 + innerRadius * Math.sin(angle);
			const x2 = 50 + outerRadius * Math.cos(angle);
			const y2 = 50 + outerRadius * Math.sin(angle);

			return { x1, y1, x2, y2, isHourMarker };
		});
	}, []);

	const classNames = [
		'analog-clock',
		isInteractive ? 'analog-clock--interactive' : '',
		showBackground ? 'analog-clock--with-background' : '',
		`analog-clock--${theme}`,
		isDraggingHour || isDraggingMinute ? 'analog-clock--dragging' : '',
		className,
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div className={classNames}>
			{showBackground && (
				<div className="analog-clock__background">
					<BackgroundComponent />
				</div>
			)}

			<div className="analog-clock__clock-container">
				<svg ref={clockRef} viewBox="0 0 100 100" className="analog-clock__svg">
					{minuteMarkers.map((marker, i) => (
						<line
							key={i}
							x1={marker.x1}
							y1={marker.y1}
							x2={marker.x2}
							y2={marker.y2}
							className={
								marker.isHourMarker
									? 'analog-clock__marker analog-clock__marker--hour'
									: 'analog-clock__marker'
							}
						/>
					))}

					{clockNumbers.map(({ number, x, y }) => (
						<text
							key={number}
							x={x}
							y={y}
							className="analog-clock__number"
							textAnchor="middle"
							dominantBaseline="central"
							style={{ fontFamily: 'ModernEra, Helvetica, Arial, sans-serif' }}
						>
							{number}
						</text>
					))}

					<g
						className="analog-clock__hand-group"
						onMouseDown={handleMinuteMouseDown}
						onTouchStart={handleMinuteTouchStart}
						style={{
							transform: `rotate(${angles.minute}deg)`,
							transformOrigin: '50% 50%',
							cursor: isInteractive ? (isDraggingMinute ? 'grabbing' : 'grab') : 'default',
						}}
					>
						<line
							x1="50"
							y1="50"
							x2="50"
							y2="15"
							className="analog-clock__hand-hitbox"
							strokeWidth="8"
							stroke="transparent"
							pointerEvents="stroke"
						/>
						<line
							x1="50"
							y1="50"
							x2="50"
							y2="15"
							className="analog-clock__hand analog-clock__hand--minute"
						/>
						{isInteractive && (
							<foreignObject x="43" y="8" width="14" height="14" overflow="visible">
								<DragIndicator className="analog-clock__hand-indicator" />
							</foreignObject>
						)}
					</g>

					<g
						className="analog-clock__hand-group"
						onMouseDown={handleHourMouseDown}
						onTouchStart={handleHourTouchStart}
						style={{
							transform: `rotate(${angles.hour}deg)`,
							transformOrigin: '50% 50%',
							cursor: isInteractive ? (isDraggingHour ? 'grabbing' : 'grab') : 'default',
						}}
					>
						<line
							x1="50"
							y1="50"
							x2="50"
							y2="25"
							className="analog-clock__hand-hitbox"
							strokeWidth="8"
							stroke="transparent"
							pointerEvents="stroke"
						/>
						<line x1="50" y1="50" x2="50" y2="25" className="analog-clock__hand analog-clock__hand--hour" />
						{isInteractive && (
							<foreignObject x="43" y="18" width="14" height="14" overflow="visible">
								<DragIndicator className="analog-clock__hand-indicator" />
							</foreignObject>
						)}
					</g>
				</svg>
			</div>
		</div>
	);
};

export default AnalogClock;
